import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { foldersService } from '../services/folders.service';
import { documentsService } from '../services/documents.service';
import { postsService } from '../services/posts.service';
import { useAuth } from '../context/AuthContext';
import { Folder } from '../types';
import Header from '../components/Layout/Header';
import MobileSidebar from '../components/Layout/mobileSidebar';
import { isCoAdminOrAdmin } from '../utils/roles';
import './Documents.css';

const MOBILE_TABLET_BREAKPOINT = 1024; // px
const MAX_POST_MEDIA_BYTES = 100 * 1024 * 1024;

type PendingDelete = { kind: 'DOCUMENT' | 'FOLDER'; id: string };
type SearchResultItem = {
  folderId: string;
  folderName: string;
  doc: NonNullable<Folder['documents']>[number];
};

const Documents: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [highlightedDocumentId, setHighlightedDocumentId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isMobileOrTablet, setIsMobileOrTablet] = useState<boolean>(
    () =>
      typeof window !== 'undefined'
        ? window.innerWidth <= MOBILE_TABLET_BREAKPOINT
        : true
  );

  // Upload form state
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadCaption, setUploadCaption] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [postToFeed, setPostToFeed] = useState(false);
  const [postImage, setPostImage] = useState<File | null>(null);
  const [postImagePreview, setPostImagePreview] = useState<string | null>(null);
  const [postCaption, setPostCaption] = useState('');
  const [shouldNotify, setShouldNotify] = useState(false);

  // Folder form state
  const [folderName, setFolderName] = useState('');
  const [folderDescription, setFolderDescription] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);

  // Error state
  const [uploadError, setUploadError] = useState('');
  const [folderError, setFolderError] = useState('');
  const [documentSearchTerm, setDocumentSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResultItem[]>([]);
  const [isSearchingAcrossFolders, setIsSearchingAcrossFolders] = useState(false);
  const [folderDocumentsCache, setFolderDocumentsCache] = useState<Record<string, Folder>>({});
  const searchRunIdRef = useRef(0);
  const [downloadingDocId, setDownloadingDocId] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState('');

  const canManage = isCoAdminOrAdmin(user?.role);

  const loadFolderDocuments = useCallback(async (folder: Folder) => {
    try {
      const folderWithDocs = await foldersService.getById(folder.id);
      setSelectedFolder(folderWithDocs);
      setFolderDocumentsCache((prev) => ({ ...prev, [folder.id]: folderWithDocs }));
      return folderWithDocs;
    } catch (error) {
      console.error('Failed to load folder documents:', error);
      return null;
    }
  }, []);

  const getFolderWithDocuments = useCallback(
    async (folder: Folder) => {
      if (folderDocumentsCache[folder.id]) {
        return folderDocumentsCache[folder.id];
      }
      try {
        const folderWithDocs = await foldersService.getById(folder.id);
        setFolderDocumentsCache((prev) => ({ ...prev, [folder.id]: folderWithDocs }));
        return folderWithDocs;
      } catch (error) {
        console.error('Failed to load folder documents for search:', error);
        return null;
      }
    },
    [folderDocumentsCache]
  );

  const loadFolders = useCallback(async () => {
    try {
      const data = await foldersService.getAll();
      // Sort oldest first (FIFO - first created appears first)
      const sorted = [...data].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );
      setFolders(sorted);
      if (sorted.length > 0 && !selectedFolder) {
        loadFolderDocuments(sorted[0]);
      }
    } catch (error) {
      console.error('Failed to load folders:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFolder, loadFolderDocuments]);

  useEffect(() => {
    loadFolders();
  }, [loadFolders]);

  useEffect(() => {
    if (!selectedFolder) return;
    setFolderDocumentsCache((prev) => ({ ...prev, [selectedFolder.id]: selectedFolder }));
  }, [selectedFolder]);

  useEffect(() => {
    return () => {
      if (postImagePreview?.startsWith('blob:')) URL.revokeObjectURL(postImagePreview);
    };
  }, [postImagePreview]);

  // Check for document highlight from navigation
  useEffect(() => {
    if (folders.length === 0) return; // Wait for folders to load

    const searchParams = new URLSearchParams(location.search);
    const docId = searchParams.get('docId');
    const folderId = searchParams.get('folderId');

    // Check location state (from PostCard navigation) - fallback
    const state = location.state as { documentId?: string; folderId?: string } | null;
    const targetDocId = docId || state?.documentId;
    const targetFolderId = folderId || state?.folderId;

    if (targetDocId && targetFolderId) {
      console.log('[Documents] Highlighting document:', { docId: targetDocId, folderId: targetFolderId });
      
      // Find and select the folder
      const folder = folders.find(f => f.id === targetFolderId);
      if (folder) {
        // Only load if not already selected or if it's a different folder
        if (!selectedFolder || selectedFolder.id !== folder.id) {
          loadFolderDocuments(folder).then((folderWithDocs) => {
            if (!folderWithDocs) return;
            
            // Set highlighted document after folder loads
            setTimeout(() => {
              console.log('[Documents] Setting highlighted document ID:', targetDocId);
              setHighlightedDocumentId(targetDocId);
              
              // Scroll to the document
              setTimeout(() => {
                const docElement = document.querySelector(`[data-document-id="${targetDocId}"]`);
                console.log('[Documents] Document element found:', docElement);
                if (docElement) {
                  docElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }, 500);
              
              // Remove highlight after 5 seconds and clean URL
              setTimeout(() => {
                setHighlightedDocumentId(null);
                // Clean URL params after highlighting
                if (window.history.replaceState) {
                  window.history.replaceState({}, '', '/documents');
                }
              }, 5000);
            }, 300);
          });
        } else {
          // Folder already selected, just highlight the document
          setTimeout(() => {
            console.log('[Documents] Setting highlighted document ID (folder already loaded):', targetDocId);
            setHighlightedDocumentId(targetDocId);
            
            setTimeout(() => {
              const docElement = document.querySelector(`[data-document-id="${targetDocId}"]`);
              if (docElement) {
                docElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
            
            setTimeout(() => {
              setHighlightedDocumentId(null);
              if (window.history.replaceState) {
                window.history.replaceState({}, '', '/documents');
              }
            }, 5000);
          }, 100);
        }
      } else {
        console.log('[Documents] Folder not found:', targetFolderId);
      }
    }
  }, [location.search, location.state, folders, selectedFolder, loadFolderDocuments]);

  useEffect(() => {
    const onResize = () =>
      setIsMobileOrTablet(window.innerWidth <= MOBILE_TABLET_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const anyModalOpen = showUploadModal || showFolderModal || !!pendingDelete;
    if (anyModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [showUploadModal, showFolderModal, pendingDelete]);

  useEffect(() => {
    const term = documentSearchTerm.trim().toLowerCase();
    const currentRunId = ++searchRunIdRef.current;

    if (!term) {
      setSearchResults([]);
      setIsSearchingAcrossFolders(false);
      return;
    }

    if (!folders.length) {
      setSearchResults([]);
      setIsSearchingAcrossFolders(false);
      return;
    }

    const searchAcrossFolders = async () => {
      setIsSearchingAcrossFolders(true);
      const results: SearchResultItem[] = [];

      for (const folder of folders) {
        const folderWithDocs =
          selectedFolder && selectedFolder.id === folder.id
            ? selectedFolder
            : await getFolderWithDocuments(folder);

        if (searchRunIdRef.current !== currentRunId) return;
        if (!folderWithDocs) continue;

        (folderWithDocs.documents || []).forEach((doc) => {
          const name = (doc.name || '').toLowerCase();
          const caption = (doc.caption || '').toLowerCase();
          if (name.includes(term) || caption.includes(term)) {
            results.push({
              folderId: folderWithDocs.id,
              folderName: folderWithDocs.name,
              doc,
            });
          }
        });
      }

      if (searchRunIdRef.current === currentRunId) {
        setSearchResults(results);
        setIsSearchingAcrossFolders(false);
      }
    };

    searchAcrossFolders();
  }, [documentSearchTerm, folders, selectedFolder, getFolderWithDocuments]);

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!folderName.trim()) return;

    setIsCreatingFolder(true);
    setFolderError('');
    try {
      const newFolder = await foldersService.create({
        name: folderName,
        description: folderDescription || undefined,
      });
      setFolderName('');
      setFolderDescription('');
      setShowFolderModal(false);
      setFolders(prev => [...prev, newFolder].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ));
      // Auto-select the newly created folder
      loadFolderDocuments(newFolder);
    } catch (error: any) {
      console.error('Failed to create folder:', error);
      setFolderError(error.response?.data?.message || 'Failed to create folder');
    } finally {
      setIsCreatingFolder(false);
    }
  };

  const handleUploadDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!uploadFile || !selectedFolder) return;

    setIsUploading(true);
    setUploadError('');
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('name', uploadFile.name);
      formData.append('folderId', selectedFolder.id);
      if (uploadCaption) formData.append('caption', uploadCaption);
      formData.append('shouldNotify', shouldNotify.toString());

      const uploadedDocument = await documentsService.upload(formData);
      
      // If post to feed is enabled, create a post
      if (postToFeed) {
        try {
          let cdnLink = '';
          
          // Upload post image if provided
          if (postImage) {
            const imageFormData = new FormData();
            imageFormData.append('file', postImage);
            const uploadResponse = await postsService.uploadImage(imageFormData);
            cdnLink = uploadResponse.url;
          }

          // Create post with link to documents page (include document ID and folder ID)
          const defaultContent = `New document "${uploadFile.name}" has been uploaded to the "${selectedFolder.name}" folder.\n\nView it in Documents: /documents?docId=${uploadedDocument.id}&folderId=${selectedFolder.id}`;
          const postContent = postCaption.trim() 
            ? `${postCaption.trim()}\n\nView in Documents: /documents?docId=${uploadedDocument.id}&folderId=${selectedFolder.id}`
            : defaultContent;
          
          await postsService.createPost({
            caption: postCaption.trim() || `New Document: ${uploadFile.name}`,
            content: postContent,
            category: 'ANNOUNCEMENT',
            cdnLink: cdnLink || undefined,
            accessScope: 'BRANCH',
            branch: user?.branch || '',
          });
        } catch (postError: any) {
          console.error('Failed to create post:', postError);
          // Don't fail the document upload if post creation fails
          setUploadError('Document uploaded, but failed to create post: ' + (postError.response?.data?.message || 'Unknown error'));
        }
      }

      // Reset form
      setUploadFile(null);
      setUploadCaption('');
      setPostToFeed(false);
      setPostImage(null);
      setPostImagePreview(null);
      setPostCaption('');
      setShouldNotify(false);
      setShowUploadModal(false);
      loadFolderDocuments(selectedFolder);
    } catch (error: any) {
      console.error('Failed to upload document:', error);
      setUploadError(error.response?.data?.message || 'Failed to upload document');
    } finally {
      setIsUploading(false);
    }
  };

  const handlePostImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > MAX_POST_MEDIA_BYTES) {
        setUploadError(`Post attachment must be under ${MAX_POST_MEDIA_BYTES / (1024 * 1024)} MB`);
        e.target.value = '';
        return;
      }
      setUploadError('');
      setPostImage(file);
      setPostImagePreview(URL.createObjectURL(file));
    }
  };

  const removePostImage = () => {
    setPostImage(null);
    setPostImagePreview(null);
  };

  const handleDeleteDocument = (docId: string) => {
    setPendingDelete({ kind: 'DOCUMENT', id: docId });
  };

  // const handleDeleteFolder = (folderId: string) => {
  //   setPendingDelete({ kind: 'FOLDER', id: folderId });
  // };

  const cancelPendingDelete = () => {
    if (isDeleting) return;
    setPendingDelete(null);
  };

  const confirmPendingDelete = async () => {
    if (!pendingDelete) return;
    setIsDeleting(true);
    try {
      if (pendingDelete.kind === 'DOCUMENT') {
        await documentsService.delete(pendingDelete.id);
        if (selectedFolder) {
          await loadFolderDocuments(selectedFolder);
        }
      } else {
        await foldersService.delete(pendingDelete.id);
        setSelectedFolder(null);
        await loadFolders();
      }
      setPendingDelete(null);
    } catch (error) {
      console.error('Failed to delete:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const openDocumentInNewTab = (doc: { cdnLink: string }) => {
    if (!doc?.cdnLink) return;
    const url = doc.cdnLink.replace(/\.r2\.devp\//g, '.r2.dev/');
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleDownloadDocument = async (
    e: React.MouseEvent,
    doc: { id: string; name: string },
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setDownloadError('');
    setDownloadingDocId(doc.id);
    try {
      await documentsService.downloadToDevice(doc.id, doc.name);
    } catch {
      setDownloadError('Could not download this file. Please try again.');
    } finally {
      setDownloadingDocId(null);
    }
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return '📄';
      case 'doc':
      case 'docx': return '📝';
      case 'xls':
      case 'xlsx': return '📊';
      case 'ppt':
      case 'pptx': return '📑';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif': return '🖼️';
      case 'zip':
      case 'rar': return '📦';
      default: return '📎';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const normalizedSearchTerm = documentSearchTerm.trim().toLowerCase();
  const isSearchMode = normalizedSearchTerm.length > 0;

  const filteredDocuments = (selectedFolder?.documents || []).filter((doc) => {
    if (!normalizedSearchTerm) return true;
    const name = (doc.name || '').toLowerCase();
    const caption = (doc.caption || '').toLowerCase();
    return name.includes(normalizedSearchTerm) || caption.includes(normalizedSearchTerm);
  });

  function requestDeleteFolder(id: string) {
    setPendingDelete({ kind: 'FOLDER', id });
  }

  return (
    <div className="documents-page">
      <Header />
      {/* Hamburger for mobile/tablet only */}
      {isMobileOrTablet && (
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          onClick={() => setMobileSidebarOpen(true)}
        >
          <span className="hamburger-line" />
          <span className="hamburger-line" />
          <span className="hamburger-line" />
        </button>
      )}
      <div className="documents-container">
        {/* Sidebar - Folders */}
        <aside className="folders-sidebar">
          <div className="sidebar-header">
            <h3>Folders</h3>
            {canManage && (
              <button
                className="add-folder-btn"
                onClick={() => {
                  setFolderError('');
                  setShowFolderModal(true);
                }}
                aria-label="Add folder"
              >
              </button>
            )}
          </div>

          <div className="folders-list">
            {isLoading ? (
              <div className="loading-text">Loading...</div>
            ) : folders.length === 0 ? (
              <div className="empty-text">No folders yet</div>
            ) : (
              folders.map((folder) => (
                <div
                  key={folder.id}
                  className={`folder-item ${!isSearchMode && selectedFolder?.id === folder.id ? 'active' : ''}`}
                  onClick={() => {
                    setDocumentSearchTerm('');
                    loadFolderDocuments(folder);
                  }}
                >
                  <span className="folder-icon">📁</span>
                  <span className="folder-name">{folder.name}</span>
                  {canManage && (
                    <button
                      className="delete-folder-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        requestDeleteFolder(folder.id);
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
        </aside>

        {/* Main Content - Documents */}
        <main className="documents-main">
          <div className="documents-header">
            {selectedFolder && (
              <div className="documents-toolbar">
                <div className="documents-search">
                  <input
                    type="text"
                    className="documents-search-input"
                    placeholder="Search documents by name or caption..."
                    value={documentSearchTerm}
                    onChange={(e) => setDocumentSearchTerm(e.target.value)}
                    aria-label="Search documents"
                  />
                </div>
                {canManage && (
                  <button
                    className="upload-btn"
                    onClick={() => {
                      setUploadError('');
                      setShowUploadModal(true);
                    }}
                  >
                    + Upload Document
                  </button>
                )}
              </div>
            )}

            <div className="header-info">
              <h2>{selectedFolder?.name || 'Select a Folder'}</h2>
              {selectedFolder?.description && (
                <p className="folder-description">{selectedFolder.description}</p>
              )}
            </div>
          </div>

          {downloadError ? (
            <div className="error-message documents-download-error" role="alert">
              {downloadError}
            </div>
          ) : null}

          <div className={`documents-grid ${isSearchMode ? 'documents-grid--search' : ''}`}>
            {isSearchMode ? (
              isSearchingAcrossFolders ? (
                <div className="no-documents">
                  <span className="empty-icon">⏳</span>
                  <p>Searching documents...</p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="no-documents">
                  <span className="empty-icon">🔎</span>
                  <p>No documents matched your search</p>
                </div>
              ) : (
                searchResults.map(({ folderId, folderName, doc }) => (
                  <div
                    key={`${folderId}-${doc.id}`}
                    className={`search-result-card ${highlightedDocumentId === doc.id ? 'highlighted' : ''}`}
                    data-document-id={doc.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => openDocumentInNewTab(doc)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openDocumentInNewTab(doc);
                      }
                    }}
                  >
                    <div className="search-result-main">
                      <div className="doc-info">
                        <span className="doc-folder-name">{folderName}</span>
                        <h4 className="doc-name" title={doc.name}>{doc.name}</h4>
                        {doc.caption && <p className="doc-caption">{doc.caption}</p>}
                        <span className="doc-date">{formatDate(doc.uploadedAt)}</span>
                      </div>
                    </div>
                    <div className="doc-actions">
                      <button
                        type="button"
                        className="doc-action-btn download"
                        title="Download"
                        aria-label="Download"
                        disabled={downloadingDocId === doc.id}
                        onClick={(e) => void handleDownloadDocument(e, doc)}
                      >
                        {downloadingDocId === doc.id ? '…' : '⬇️'}
                      </button>
                    </div>
                  </div>
                ))
              )
            ) : !selectedFolder ? (
              <div className="no-folder-selected">
                <span className="empty-icon">📂</span>
                <p>Select a folder to view documents</p>
              </div>
            ) : !selectedFolder.documents || selectedFolder.documents.length === 0 ? (
              <div className="no-documents">
                <span className="empty-icon">📄</span>
                <p>No documents in this folder</p>
                {canManage && (
                  <button
                    className="upload-btn-secondary"
                    onClick={() => {
                      setUploadError('');
                      setShowUploadModal(true);
                    }}
                  >
                    Upload First Document
                  </button>
                )}
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="no-documents">
                <span className="empty-icon">🔎</span>
                <p>No documents matched your search</p>
              </div>
            ) : (
              filteredDocuments.map((doc) => (
                <div 
                  key={doc.id} 
                  className={`document-card ${highlightedDocumentId === doc.id ? 'highlighted' : ''}`}
                  data-document-id={doc.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openDocumentInNewTab(doc)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openDocumentInNewTab(doc);
                    }
                  }}
                >
                  <div className="doc-icon">{getFileIcon(doc.name)}</div>
                  <div className="doc-info">
                    <h4 className="doc-name" title={doc.name}>{doc.name}</h4>
                    {doc.caption && <p className="doc-caption">{doc.caption}</p>}
                    <span className="doc-date">{formatDate(doc.uploadedAt)}</span>
                  </div>
                  <div className="doc-actions">
                    <button
                      type="button"
                      className="doc-action-btn download"
                      title="Download"
                      aria-label="Download"
                      disabled={downloadingDocId === doc.id}
                      onClick={(e) => void handleDownloadDocument(e, doc)}
                    >
                      {downloadingDocId === doc.id ? '…' : '⬇️'}
                    </button>
                    {canManage && (
                      <button
                        className="doc-action-btn delete"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDocument(doc.id);
                        }}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </main>
      </div>
      {isMobileOrTablet && mobileSidebarOpen && (
        <MobileSidebar onClose={() => setMobileSidebarOpen(false)} />
      )}

      {/* Create Folder Modal */}
      {showFolderModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Create Folder</h2>
              <button className="modal-close" onClick={() => setShowFolderModal(false)}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateFolder}>
              <div className="modal-body">
                {folderError && <div className="error-message">{folderError}</div>}
                <div className="form-group">
                  <label>Folder Name *</label>
                  <input
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder="Enter folder name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={folderDescription}
                    onChange={(e) => setFolderDescription(e.target.value)}
                    placeholder="Optional description"
                    rows={3}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => setShowFolderModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isCreatingFolder}
                >
                  {isCreatingFolder ? 'Creating...' : 'Create Folder'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Document Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Document</h2>
              <button 
                className="modal-close" 
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadFile(null);
                  setUploadCaption('');
                  setPostToFeed(false);
                  setPostImage(null);
                  setPostImagePreview(null);
                  setPostCaption('');
                  setShouldNotify(false);
                  setUploadError('');
                }}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleUploadDocument}>
              <div className="modal-body">
                {uploadError && <div className="error-message">{uploadError}</div>}
                <div className="form-group">
                  <label>File *</label>
                  <input
                    type="file"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Caption</label>
                  <input
                    type="text"
                    value={uploadCaption}
                    onChange={(e) => setUploadCaption(e.target.value)}
                    placeholder="Optional caption"
                  />
                </div>
                
                {/* Send Notification Checkbox */}
                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={shouldNotify}
                      onChange={(e) => setShouldNotify(e.target.checked)}
                    />
                    <span>Send notification to all users</span>
                  </label>
                </div>

                {/* Post to Feed Toggle */}
                <div className="form-group">
                  <label className="toggle-label">
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <input
                        type="checkbox"
                        checked={postToFeed}
                        onChange={(e) => {
                          setPostToFeed(e.target.checked);
                          if (!e.target.checked) {
                            setPostImage(null);
                            setPostImagePreview(null);
                            setPostCaption('');
                          }
                        }}
                        className="toggle-checkbox"
                      />
                      <span className="toggle-slider"></span>
                    </div>
                    <span className="toggle-text">Post to Feed</span>
                  </label>
                </div>

                {/* Post Image Upload - Only show when postToFeed is enabled */}
                {postToFeed && (
                  <>
                    <div className="form-group">
                      <label>Post image or video</label>
                      {postImagePreview ? (
                        <div className="image-preview-container">
                          {postImage?.type.startsWith('video/') ? (
                            <video
                              src={postImagePreview}
                              className="image-preview image-preview--video"
                              controls
                              muted
                              playsInline
                            />
                          ) : (
                            <img src={postImagePreview} alt="Preview" className="image-preview" />
                          )}
                          <button
                            type="button"
                            onClick={removePostImage}
                            className="remove-image-btn"
                          >
                            Remove attachment
                          </button>
                        </div>
                      ) : (
                        <input
                          type="file"
                          accept="image/*,video/*"
                          onChange={handlePostImageChange}
                        />
                      )}
                    </div>
                    <div className="form-group">
                      <label>Post Caption</label>
                      <input
                        type="text"
                        value={postCaption}
                        onChange={(e) => setPostCaption(e.target.value)}
                        placeholder="Optional post caption (defaults to document name)"
                      />
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => {
                    setShowUploadModal(false);
                    setUploadFile(null);
                    setUploadCaption('');
                    setPostToFeed(false);
                    setPostImage(null);
                    setPostImagePreview(null);
                    setPostCaption('');
                    setShouldNotify(false);
                    setUploadError('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={isUploading || !uploadFile}
                >
                  {isUploading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {pendingDelete && (
        <div className="modal-overlay" onClick={cancelPendingDelete}>
          <div
            className="modal-content delete-confirmation-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>Delete Item</h2>
              <button className="modal-close" onClick={cancelPendingDelete}>×</button>
            </div>
            <div className="modal-body">
              <p className="delete-confirmation-message">
                {pendingDelete.kind === 'FOLDER'
                  ? 'Are you sure you want to delete this folder and all its documents?'
                  : 'Are you sure you want to delete this document?'}
              </p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={cancelPendingDelete}
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-delete"
                onClick={confirmPendingDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Documents;
